import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { usePreferences } from '../contexts/PreferencesContext';
import { apiGet } from '../lib/api';

interface ActivityMap {
  [date: string]: { meetings: number; tasks: number; suggested?: number };
}

function formatDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getLocalToday(): string {
  return formatDateKey(new Date());
}

/** Get the start of the week containing `date`, using the given weekStartDay (0=Sun, 1=Mon). */
function getWeekStart(date: Date, weekStartDay: number): Date {
  const d = new Date(date);
  const diff = (d.getDay() - weekStartDay + 7) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

/** Get array of 7 dates for the week containing `date`. */
function getWeekDays(date: Date, weekStartDay: number): Date[] {
  const start = getWeekStart(date, weekStartDay);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });
}

/** Get a 6×7 grid of dates for the given month. */
function getMonthGrid(year: number, month: number, weekStartDay: number): Date[][] {
  const firstOfMonth = new Date(year, month, 1);
  const start = getWeekStart(firstOfMonth, weekStartDay);
  const rows: Date[][] = [];
  const current = new Date(start);
  for (let row = 0; row < 6; row++) {
    const week: Date[] = [];
    for (let col = 0; col < 7; col++) {
      week.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    rows.push(week);
  }
  return rows;
}

const DAY_LABELS_SUN = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const DAY_LABELS_MON = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function CalendarWidget() {
  const navigate = useNavigate();
  const { prefs } = usePreferences();
  const showWidget = prefs['sidebar.calendarWidget'] !== 'false';
  const weekStartDay = parseInt(prefs['sidebar.weekStartDay'] || '0', 10);
  const dayLabels = weekStartDay === 1 ? DAY_LABELS_MON : DAY_LABELS_SUN;

  const [expanded, setExpanded] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [activity, setActivity] = useState<ActivityMap>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const today = getLocalToday();

  const weekDays = useMemo(() => getWeekDays(new Date(), weekStartDay), [weekStartDay]);

  const monthGrid = useMemo(
    () => getMonthGrid(viewMonth.year, viewMonth.month, weekStartDay),
    [viewMonth.year, viewMonth.month, weekStartDay]
  );

  // Compute the date range needed for fetching activity data
  const fetchRange = useMemo(() => {
    if (expanded) {
      const firstDay = monthGrid[0][0];
      const lastDay = monthGrid[monthGrid.length - 1][6];
      return { from: formatDateKey(firstDay), to: formatDateKey(lastDay) };
    }
    return { from: formatDateKey(weekDays[0]), to: formatDateKey(weekDays[6]) };
  }, [expanded, monthGrid, weekDays]);

  // Fetch activity data
  useEffect(() => {
    if (!showWidget) return;
    let cancelled = false;
    apiGet<ActivityMap>(`/calendar/activity?from=${fetchRange.from}&to=${fetchRange.to}`)
      .then(data => { if (!cancelled) setActivity(prev => ({ ...prev, ...data })); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [fetchRange.from, fetchRange.to, showWidget]);

  const handleDayClick = useCallback((dateKey: string) => {
    navigate(`/day/${dateKey}`);
  }, [navigate]);

  const handleMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setExpanded(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => setExpanded(false), 200);
  }, []);

  const handleToggleExpand = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  function goToToday() {
    const now = new Date();
    setViewMonth({ year: now.getFullYear(), month: now.getMonth() });
  }

  function prevMonth() {
    setViewMonth(prev => {
      const m = prev.month - 1;
      return m < 0 ? { year: prev.year - 1, month: 11 } : { year: prev.year, month: m };
    });
  }

  function nextMonth() {
    setViewMonth(prev => {
      const m = prev.month + 1;
      return m > 11 ? { year: prev.year + 1, month: 0 } : { year: prev.year, month: m };
    });
  }

  if (!showWidget) return null;

  function DayCell({ date, isCurrentMonth = true }: { date: Date; isCurrentMonth?: boolean }) {
    const dateKey = formatDateKey(date);
    const isToday = dateKey === today;
    const isPast = dateKey < today;
    const act = activity[dateKey];
    const hasDot = act && (act.meetings > 0 || act.tasks > 0);
    const hasSuggested = act && (act.suggested ?? 0) > 0 && !hasDot;

    return (
      <button
        onClick={() => handleDayClick(dateKey)}
        className={`
          relative flex flex-col items-center justify-center w-full aspect-square rounded-md text-[10px] leading-none transition-colors
          ${isToday ? 'bg-accent text-white font-bold' : ''}
          ${!isToday && isPast ? 'text-text-muted' : ''}
          ${!isToday && !isPast ? 'text-text-secondary' : ''}
          ${!isCurrentMonth ? 'opacity-30' : ''}
          hover:bg-bg-hover
        `}
      >
        {date.getDate()}
        {hasDot && (
          <span className={`absolute bottom-0.5 w-1 h-1 rounded-full ${isToday ? 'bg-white' : 'bg-accent'}`} />
        )}
        {hasSuggested && (
          <span className="absolute bottom-0.5 w-1 h-1 rounded-full border border-accent bg-transparent" />
        )}
      </button>
    );
  }

  return (
    <div
      ref={containerRef}
      className="px-2 py-2"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Month/Year label */}
      <button
        onClick={handleToggleExpand}
        className="w-full text-center text-[10px] font-medium uppercase tracking-wider text-text-muted mb-1 hover:text-text-secondary transition-colors cursor-pointer"
      >
        {MONTH_NAMES[viewMonth.month]} {viewMonth.year}
      </button>

      {/* Week strip (always visible) */}
      {!expanded && (
        <div>
          <div className="grid grid-cols-7 gap-0.5 mb-0.5">
            {dayLabels.map((label, i) => (
              <span key={i} className="text-center text-[8px] text-text-muted font-medium">
                {label}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {weekDays.map(d => (
              <DayCell key={formatDateKey(d)} date={d} />
            ))}
          </div>
        </div>
      )}

      {/* Expanded month view */}
      {expanded && (
        <div className="animate-in slide-in-from-bottom-2 duration-200">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-1">
            <button onClick={prevMonth} className="p-0.5 rounded text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors">
              <ChevronLeft size={12} />
            </button>
            <button onClick={goToToday} className="text-[9px] text-accent hover:text-accent-hover transition-colors">
              Today
            </button>
            <button onClick={nextMonth} className="p-0.5 rounded text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors">
              <ChevronRight size={12} />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 gap-0.5 mb-0.5">
            {dayLabels.map((label, i) => (
              <span key={i} className="text-center text-[8px] text-text-muted font-medium">
                {label}
              </span>
            ))}
          </div>

          {/* Month grid */}
          {monthGrid.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 gap-0.5">
              {week.map(d => (
                <DayCell
                  key={formatDateKey(d)}
                  date={d}
                  isCurrentMonth={d.getMonth() === viewMonth.month}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
